<template>
  <div>
    <Container class="home">
      <div class="home__slider">
        <Slider v-if="promotions" :promotions="promotions" />
        <h1 class="home-h1" style="display: none">
          {{ $t('common.title', { domain }) }}
        </h1>
      </div>

      <div class="home__mobile-menu">
        <div class="show-from-mobile show-until-desktop">
          <div class="home__mobile-menu-sale-link">
            <SaleLinkForZSU size="large" />
          </div>

          <a
            class="catalog-button"
            @click.prevent="isOpenedMobileMenu = !isOpenedMobileMenu"
          >
            <span class="catalog-button__icon icon-menu" />
            {{ $t('catalog.menu') }}
          </a>
        </div>

        <CatalogMenuMobile
          :categories="categories"
          :categories-map="categoriesMap"
          :is-opened="isOpenedMobileMenu"
          @close-menu="isOpenedMobileMenu = false"
        />

        <div
          ref="sidebarMask"
          v-touch:swipe.left="onSwipeOverlay"
          class="home__mobile-menu-mask z-index__31"
          :class="{ active: isOpenedMobileMenu }"
          @click="isOpenedMobileMenu = false"
        />
      </div>
    </Container>

    <Container class="without-padding-from-mobile">
      <HomeRubricPopular />

      <LazyVisibleHomeRubricBonuses hydrate-on-visible />

      <LazyVisibleHomeRubricRecently hydrate-on-visible />

      <LazyVisibleHomeRubricMostReviewed hydrate-on-visible/>

      <client-only>
        <LazyTimeUnsubscribeModal :hydrate-after="2000" />
      </client-only>
    </Container>
  </div>
</template>

<script setup lang="ts">
import type { PromotionsSlide } from '~/types/promotion'

const { domain } = useRuntimeConfig().public
const commonCategoriesStore = useCommonCategoriesStore()

const {
  list: categories,
  listMap: categoriesMap
} = storeToRefs(commonCategoriesStore)

const isOpenedMobileMenu = shallowRef(false)
const sidebarMask = ref()
const i18n = useI18n()

const {
  stopScrollSubscribe,
  stopScrollUnsubscribe
} = useOverflowed()

const onSwipeOverlay = () => {
  isOpenedMobileMenu.value = false
}

watch(isOpenedMobileMenu, (opened: boolean) => {
  if (opened) {
    stopScrollSubscribe(sidebarMask.value)
  } else {
    stopScrollUnsubscribe(sidebarMask.value)
  }
})

const { data: promotions } = useLazyAPI<PromotionsSlide[]>('/api/promotions/pages/main/', {
  parseResponse(responseText) {
    return JSON.parse(responseText).results.map((promotion: PromotionsSlide) => {
      return {
        ...promotion,
        link: promotion.link.replace(/https?:\/\/synthetic.ua/, '')
      }
    })
  }
})

const organizationJSOLD = computed(() => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Synthetic',
    url: 'https://synthetic.ua/',
    logo: 'https://synthetic.ua/logo-synthetic.svg',
    description: i18n.t('common.simple_description'),
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'вул. Винниченка 26',
      addressLocality: 'Луцьк',
      postalCode: '43000',
      addressCountry: 'UA'
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '380955947348',
        contactType: 'Підтримка',
        areaServed: 'UA'
      }
    ],
    sameAs: [
      'https://www.facebook.com/synthethic.ua/',
      'https://www.instagram.com/synthetic_ua/',
      'https://www.tiktok.com/@synthetic_ua',
      'https://www.youtube.com/channel/UCStfpCVDa03L-dJyJLhDG2w'
    ]
  }
})

useHead({
  script: [{ innerHTML: JSON.stringify(organizationJSOLD.value), type: 'application/ld+json' }]
})

onBeforeUnmount(() => {
  stopScrollUnsubscribe(sidebarMask.value)
})
</script>

<style lang="scss" scoped>
.home-h1 {
  display: none;
  font-size: 2rem;
  line-height: 2.4rem;
  padding: 1rem;
}

.home {
  @media only screen and (min-width: 1024px) {
    min-height: 49.5rem;
  }

  &__slider {
    padding-top: 0.8rem;

    @media only screen and (min-width: 1024px) {
      padding-left: 28rem;
      padding-top: 2.8rem;
    }
  }

  &__mobile-menu {
    &-mask {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 100%;
      background-color: rgba(0, 0, 0, 0.5);

      &.active {
        display: block;
      }
    }

    @media only screen and (max-width: 1024px) {
      margin-top: 2.4rem;
    }
  }

  &__mobile-menu-sale-link {
    margin-bottom: 2.4rem;
  }

  &__selected-city {
    margin: 2.4rem 0 0;

    @media only screen and (min-width: 1024px) {
      margin: 4rem auto 0;
    }
  }
}
.popular {
  margin-top: 4.5rem;
  &__title {
    font-size: 2.6rem;
    font-weight: 500;
    margin-bottom: 2rem;

    @media only screen and (max-width: 740px) {
      font-size: 2rem;
      margin-bottom: 1rem;
    }
  }
  &__list {
    margin: 0 -0.4rem;
  }
}
.catalog-button {
  border: 0.2rem solid $primary-color;
  cursor: pointer;
  width: 100%;
  height: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: 1.6rem;
  line-height: 1.9rem;
  border-radius: 8px;
  background-color: $white-color;

  &__icon {
    margin-right: 0.8rem;
    font-size: 1.6rem;
  }

  @media only screen and (min-width: 740px) and (max-width: 1024px) {
    height: 4.8rem;
  }
}
</style>
